import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ModulePanel from '../../ui/ModulePanel/ModulePanel'
import Spinner from '../../ui/Spinner/Spinner'
import DataTable from '../../ui/DataTable/DataTable'
import StatusMessage, { useStatusMessage } from '../../ui/StatusMessage/StatusMessage'
import { listBlogTags, upsertBlogTag } from '../../api/blog/blogApi'
import { listLocalTags, upsertLocalTag } from '../../api/blog/blogLocalStore'
import LinksRowActions from '../Marketing/Links/LinksRowActions'
import '../Marketing/Links/LinksTable.sass'
import BlogTagPopUp from './BlogTagPopUp'
import styles from './Blog.module.scss'

function BlogTagsManager() {
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [tags, setTags] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [popUpOpen, setPopUpOpen] = useState(false)
  const [editingTag, setEditingTag] = useState(null)

  const refreshTags = useCallback(() => {
    setTags(listLocalTags())
  }, [])

  const load = useCallback(async () => {
    setIsLoading(true)
    const remote = await listBlogTags()
    if (remote.ok) {
      remote.tags.forEach((tag) => upsertLocalTag(tag))
    }
    refreshTags()
    setIsLoading(false)
  }, [refreshTags])

  useEffect(() => {
    load()
  }, [load])

  const openNew = useCallback(() => {
    setEditingTag(null)
    setPopUpOpen(true)
  }, [])

  useEffect(() => {
    window.addEventListener('blog:new-tag', openNew)
    return () => window.removeEventListener('blog:new-tag', openNew)
  }, [openNew])

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerClassName: 'TableHeadCol',
        headerName: 'Назва',
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'kind',
        headerClassName: 'TableHeadCol',
        headerName: 'Тип',
        width: 130,
        renderCell: (params) => params.row.kind === 'category' ? 'Категорія' : 'Тег',
      },
      {
        field: 'order',
        headerClassName: 'TableHeadCol',
        headerName: 'Порядок',
        width: 110,
        renderCell: (params) => params.row.kind === 'category' ? params.row.order || 0 : '—',
      },
      {
        field: 'slug',
        headerClassName: 'TableHeadCol',
        headerName: 'Slug',
        width: 220,
      },
      {
        field: 'publishedCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Опубліковано',
        width: 140,
        renderCell: (params) =>
          Number.isInteger(params.row.publishedCount) ? params.row.publishedCount : 0,
      },
      {
        field: 'actions',
        headerClassName: 'TableHeadCol',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <LinksRowActions
            onEdit={() => {
              setEditingTag(params.row)
              setPopUpOpen(true)
            }}
          />
        ),
      },
    ],
    []
  )

  const handleSave = async (draft) => {
    if (!draft.name?.trim()) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Вкажіть назву тега',
        closeAfter: 4000,
      })
      return
    }

    setIsSaving(true)
    const payload = {
      tagId: draft.id || undefined,
      name: draft.name,
      slug: draft.slug,
      kind: draft.kind,
      order: draft.order,
    }
    const remote = await upsertBlogTag(payload)

    if (remote.ok && remote.tag) {
      upsertLocalTag(remote.tag)
      setIsSaving(false)
      setPopUpOpen(false)
      setEditingTag(null)
      refreshTags()
      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Збережено',
        closeAfter: 3000,
      })
      return
    }

    const notDeployed = typeof remote.message === 'string' && remote.message.includes('not deployed')
    if (notDeployed) {
      upsertLocalTag({
        id: draft.id,
        name: draft.name,
        slug: draft.slug,
        kind: draft.kind,
        order: draft.order,
        publishedCount: draft.publishedCount || 0,
      })
      setIsSaving(false)
      setPopUpOpen(false)
      setEditingTag(null)
      refreshTags()
      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Збережено локально',
        closeAfter: 3000,
      })
      return
    }

    setIsSaving(false)
    setStatusMessage({
      show: true,
      type: 'fail',
      message: remote.message || 'Не вдалося зберегти тег',
      closeAfter: 5000,
    })
  }

  return (
    <>
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}

      <BlogTagPopUp
        open={popUpOpen}
        initialTag={editingTag}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) return
          setPopUpOpen(false)
          setEditingTag(null)
        }}
        onSave={handleSave}
      />

      <ModulePanel className={styles.panel} title="Категорії та теги">
        {isLoading ? (
          <div className={styles.empty}>
            <Spinner type="module" />
          </div>
        ) : tags.length === 0 ? (
          <p className={styles.empty}>Категорій і тегів ще немає. Натисніть «+», щоб додати.</p>
        ) : (
          <div className={`${styles.table} MarketingLinks-Table`}>
            <DataTable
              table={{
                rows: tags,
                columns,
                autoHeight: true,
                disableRowSelectionOnClick: true,
              }}
            />
          </div>
        )}
      </ModulePanel>
    </>
  )
}

export default BlogTagsManager
